import { AbstractComponent } from "appworks/components/abstract-component";
import { Components } from "appworks/components/components";
import { Layers } from "appworks/graphics/layers/layers";
import { gameState } from "appworks/model/game-state";
import { Record } from "appworks/model/gameplay/records/record";
import { Services } from "appworks/services/services";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Sequence } from "appworks/utils/contracts/sequence";
import { Timer } from "appworks/utils/timer";
import { gamevy } from "gamevy/gamevy";
import { gamevyModel } from "gamevy/gamevy-model";
import { Signal } from "signals";
import { slotModel } from "slotworks/model/slot-model";
import { CommonUIAlertComponent } from "./common-ui-alert-component";

export class GamevyFreeGamesComponent extends AbstractComponent {
    public onFreeGamesStart: Signal = new Signal();
    public onFreeGamesEnd: Signal = new Signal();
    public paused: boolean = false;

    protected layer: Layers;
    protected active: boolean = false;
    protected beginning: boolean = false;
    protected exiting: boolean = false;

    public init(): void {
        this.active = false;
        this.layer = Layers.get("BetBar");

        if (gamevyModel.getTotalFreeGames() > 0 && !gameState.getCurrentGame().isRecovered) {
            uiFlags.set(UIFlag.FREE_BETS, true);
        }

        if (!gameState.getCurrentGame().isRecovered) {
            this.begin().execute();
        }
    }

    public decrementFreespins(): void {
        const freegames: number = gamevyModel.getFreeGames(Services.bet.getTotalStake());
        if (freegames > 0 && this.active) {
            this.updateValue(freegames - 1);
        }
    }

    public waitForAlertToComplete(): Contract<void> {
        return new Contract((resolve) => {
            if (this.exiting || this.beginning) {
                const interval = Timer.setInterval(() => {
                    if (!this.exiting && !this.beginning) {
                        Timer.clearInterval(interval);
                        resolve();
                    }
                }, 100);
            } else {
                resolve();
            }
        });
    }

    public update(ignoreGamble: boolean = false): Contract<void> {
        const record: Record = gameState.getCurrentGame().getCurrentRecord();
        const gamble: boolean = (record) ? record.gamble : false;
        const { bet } = slotModel.read();
        if (!this.paused && (ignoreGamble || !gamble)) {
            if (gamevyModel.getFreeGames(bet.creditSizes.currentValue) > 0) {
                if (this.active) {
                    uiFlags.set(UIFlag.FREE_BETS, true);
                    this.updateValue(gamevyModel.getTotalFreeGames());
                    this.updateTotal(gamevyModel.getFreegamesWinnings());
                    return Contract.empty();
                } else {
                    return this.begin();
                }
            } else {
                return this.end();
            }
        } else {
            return Contract.empty();
        }
    }

    protected begin(): Contract<void> {
        const freegames: number = gamevyModel.getTotalFreeGames();
        if (freegames > 0) {
            this.active = true;
            this.beginning = true;
            this.checkForNextBet();
            return new Sequence([
                () => Contract.wrap(() => Services.autoplay.stop()),
                () => this.alert.freegamesInitial(freegames),
                () => this.update(true),
                () => Contract.wrap(() => {
                    gameState.autoDeductStake = false;
                    this.beginning = false;
                    this.onFreeGamesStart.dispatch();
                })
            ]);
        } else {
            return Contract.empty();
        }
    }

    protected end(): Contract<void> {
        if (this.active) {
            if (this.checkForNextBet()) {
                return this.update();
            } else {
                this.active = false;
                this.exiting = true;
                return new Sequence([
                    () => Contract.wrap(() => Services.autoplay.stop()),
                    () => Contract.wrap(() => {
                        this.updateValue(gamevyModel.getTotalFreeGames());
                        this.updateTotal(gamevyModel.getFreegamesWinnings());
                    }),
                    () => this.alert.freegamesComplete(gamevyModel.getFreegamesWinnings()),
                    () => Contract.wrap(() => {
                        gameState.autoDeductStake = true;
                        this.exiting = false;
                        gamevyModel.clearFreeGamesWinnings();
                        uiFlags.set(UIFlag.FREE_BETS, false);
                        this.onFreeGamesEnd.dispatch();

                        // Reset bet to default if it's invalid
                        const { bet } = slotModel.read();
                        const { currentValue, values, defaultValue } = bet.creditSizes;
                        if (values.indexOf(currentValue) === -1) {
                            Services.bet.setCreditSize(defaultValue);
                        }
                    })
                ]);
            }
        } else {
            return this.begin();
        }
    }

    protected checkForNextBet(): boolean {
        const nextStake = gamevyModel.getNextFreeGamesStake();
        const { bet } = slotModel.read();
        if (nextStake) {
            bet.creditSizes.currentValue = nextStake;
            slotModel.write({ bet });
            gamevy.commonUI.updateBet({ amount: nextStake });
            return true;
        } else {
            return false;
        }
    }

    protected updateValue(freegames: number): void {
        const value = this.layer.getText("gamevy_free_games_count_value");
        if (value) {
            value.text = freegames.toString();
        }

        const label = this.layer.getText("gamevy_free_games_count_label");
        if (label) {
            label.text = Services.translations.get("freerounds_remaining", { x: freegames.toString() });

        }
    }

    protected updateTotal(total: number): void {
        const value = this.layer.getText("gamevy_free_games_total_value");
        if (value) {
            value.text = Services.currency.format(total);
        }

        const label = this.layer.getText("gamevy_free_games_total_label");
        if (label) {
            label.text = Services.translations.get("freerounds_totalwin", { x: Services.currency.format(total) });
        }
    }

    private get alert(): CommonUIAlertComponent {
        return Components.get(CommonUIAlertComponent);
    }
}
